import { ItemsDetailForm } from "@/features/item/components/form/ItemsDetailForm";
import { LocationDetailForm } from "@/features/location/detail/LocationDetailForm";
import { useModal } from "@/features/modal/ModalStackManager";
import { PurchaseOrderItemsDetailForm } from "@/features/purchaseOrderItems/form/purchaseOrderItemDetailForm";
import { LinkAnchor } from "@/features/standardLayout/LinkAnchor";
import { TransportationDetailForm } from "@/features/transportation/form/transportationDetailForm";
import { MantineReactCommonTable } from "@/features/ui/mantineTable";
import timeUtil from "@/utils/timeUtil";
import { Flex, Text } from "@mantine/core";
import {
  WmsLogsGet200ResponseRowsInner,
  WmsLogsGet200ResponseRowsInnerActionTypeEnum,
} from "@sizlcorp/sizl-api-document/dist/models";
import {
  MRT_ColumnDef,
  MRT_ColumnFiltersState,
  MRT_PaginationState,
  MRT_SortingState,
} from "mantine-react-table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

interface StockPlanWmsLogTabProps {
  wmsLog: WmsLogsGet200ResponseRowsInner[];
}
export const StockPlanWmsLogTab = (params: StockPlanWmsLogTabProps) => {
  const { wmsLog } = params;

  const { t } = useTranslation();
  const { openModal } = useModal();
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 1,
    pageSize: 10,
  });
  // 각 컬럼 sort
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  // 각 컬럼 filter
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);
  // 전체 검색 (단, serchFields에 필드를 직접 입력해줘야함.)
  const [globalFilter, setGlobalFilter] = useState("");
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  // sort를 id 별 string으로 만들어줌
  const sortString = sorting.map(({ id, desc }) => (desc ? `-${id}` : id)).join(",");

  // filter를 id 별 like를 사용하여 단어가 포함되어있는지 확인
  const filterQuery: { [key: string]: { $iLike: string } } = columnFilters.reduce<{
    [key: string]: { $iLike: string };
  }>((acc, filter) => {
    acc[filter.id] = { $iLike: `%${filter.value}%` as string };
    return acc;
  }, {});

  const columns = useMemo<MRT_ColumnDef<WmsLogsGet200ResponseRowsInner>[]>(
    () => [
      {
        accessorKey: "id",
        header: t("wms.logId"),
        size: 50,
      },
      {
        accessorKey: "createdAt",
        header: t("stockPlan.actualDate"),
        Cell: ({ row }) => {
          return <Text>{timeUtil(row.original.createdAt ?? "")}</Text>;
        },
      },
      {
        accessorKey: "actionParam.params.itemCode",
        header: t("item.code"),
        Cell: ({ row }) => {
          return (
            <LinkAnchor
              onClick={() => {
                openModal(
                  <ItemsDetailForm itemCode={row.original.actionParam?.params?.itemCode} />,
                  null,
                  t("common.detail", { item: t("item.item") }), // 아이템 상세
                );
              }}
            >
              {row.original.actionParam?.params?.itemCode}
            </LinkAnchor>
          );
        },
      },
      {
        accessorKey: "actionParam.params.locationCode",
        header: t("location.code"),
        Cell: ({ row }) => {
          return (
            <LinkAnchor
              onClick={() => {
                openModal(
                  <LocationDetailForm
                    locationCode={row.original.actionParam?.params?.locationCode}
                  />,
                  null,
                  t("common.detail", { item: t("transportation.transportation") }), // 로케이션 상세
                );
              }}
            >
              {row.original.actionParam?.params?.locationCode}
            </LinkAnchor>
          );
        },
      },
      {
        accessorKey: "actionType",
        header: t("wms.actionType"),
        size: 100,
        Cell: ({ row }) => {
          return (
            <Flex>
              {t(row.original.actionType as WmsLogsGet200ResponseRowsInnerActionTypeEnum)}
            </Flex>
          );
        },
      },
      {
        accessorKey: "actionParam.params.lotId",
        header: t("lot.id"),
        size: 80,
      },
      {
        accessorKey: "actionParam.params.lotInfo.name",
        header: t("lot.lotInfo"),
        Cell: ({ row }) => {
          const lotName = row.original.actionParam?.params?.lotInfo?.name;
          const lotExpiration = row.original.actionParam?.params?.lotInfo?.expiration;
          return (
            <Flex gap={5} align="center" direction={"column"}>
              {
                <>
                  <Text>
                    {t("lot.name")}: {lotName ?? "-"}
                  </Text>
                  <Text>
                    {t("lot.expiration")}:{" "}
                    {lotExpiration !== null ? timeUtil(lotExpiration ?? "") : "-"}
                  </Text>
                </>
              }
            </Flex>
          );
        },
      },

      {
        accessorKey: "actionParam.params.quantity",
        header: t("inventory.quantity"),
        size: 100,
      },
      {
        accessorKey: "transportationId",
        header: t("transportation.id"),
        size: 80,
        enableColumnFilter: false, // id는 검색 못하게
        Cell: ({ row }) => {
          return (
            <LinkAnchor
              onClick={() => {
                openModal(
                  <TransportationDetailForm transportationID={row.original.transportationId} />,
                  null,
                  t("common.detail", { item: t("transportation.transportation") }), // 배차 상세
                );
              }}
            >
              {row.original.transportationId}
            </LinkAnchor>
          );
        },
      },
      {
        accessorKey: "purchaseOrderItemId",
        header: t("purchaseOrder.written"),
        size: 80,
        enableColumnFilter: false, // id는 검색 못하게
        Cell: ({ row }) => {
          return (
            <LinkAnchor
              onClick={() => {
                openModal(
                  <PurchaseOrderItemsDetailForm
                    purchaseOrderItemId={row.original?.purchaseOrderItemId ?? 0}
                    pageKey={
                      row.original.actionType === "OUTGOING" ? "purchaseOrderItems" : "orderItems"
                    }
                  />,
                  null,
                  t("common.detail", { item: t("item.item") }), // 아이템 상세
                );
              }}
            >
              {row.original.purchaseOrderItemId}
            </LinkAnchor>
          );
        },
      },
    ],
    [],
  );

  return (
    <MantineReactCommonTable
      data={wmsLog as WmsLogsGet200ResponseRowsInner[]}
      columns={columns}
      enableColumnActions={false}
      pagination={pagination} // pagination
      setPagination={setPagination}
      sorting={sorting} // 정렬
      setSorting={setSorting}
      columnFilters={columnFilters} // 컬럼별 검색
      setColumnFilters={setColumnFilters}
      globalFilter={globalFilter} // 전체 검색
      setGlobalFilter={setGlobalFilter}
      enableTopToolbar={true}
    />
  );
};
